import React, { ReactNode } from 'react';
import { useController, useFieldArray, useFormContext } from 'react-hook-form';

import {
  Button,
  TitleWithDescription,
  LbFormControlLabel,
  InfoBox,
  Switch,
} from '@infoods/lifebase-ui';
import AddIcon from '@mui/icons-material/Add';

import classes from './FaqForm.module.scss';
import { EditFaqRow } from './EditFaqRow/EditFaqRow';
import { ViewFaqRow } from './ViewFaqRow/ViewFaqRow';
import { ChallengeFormValues } from '../../../interfaces/ChallengeFormValues';
import { Mode } from '../../../../general-types';

type FaqFormProps = {
  mode: Mode;
  headerActions?: ReactNode;
};

export const FaqForm = ({ mode, headerActions }: FaqFormProps) => {
  const isViewMode = mode === 'VIEW';
  const { control } = useFormContext<ChallengeFormValues>();

  const {
    field: { value: showFaqs, onChange, onBlur, ref, name: fieldName },
  } = useController({
    control,
    name: 'faqs.enabled',
    defaultValue: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'faqs.faqs',
  });

  const handleAddFaq = () => {
    append({ question: '', answer: '', questionNum: fields.length + 1 });
  };

  const handleRemoveFaq = (index: number) => {
    remove(index);
  };

  const onSwitchChange = (_: any, checked: boolean) => {
    if (fields.length === 0) {
      handleAddFaq();
    }

    onChange(checked);
  };

  const SwitchTitle = (
    <LbFormControlLabel
      control={
        <Switch
          checked={showFaqs}
          onChange={onSwitchChange}
          slotProps={{ input: { ref } }}
          name={fieldName}
          onBlur={onBlur}
          disabled={isViewMode}
        />
      }
      label={
        <TitleWithDescription
          title="FAQ"
          description="Help your customers feel confident and informed by answering common questions about your challenge."
        />
      }
      align="start"
    />
  );

  return (
    <InfoBox
      title={SwitchTitle}
      adornment={headerActions}
      hideConfig={{ show: showFaqs }}
      customContentPadding
    >
      {isViewMode ? (
        fields.map((field) => (
          <ViewFaqRow key={field.id} question={field.question} answer={field.answer} />
        ))
      ) : (
        <>
          {fields.map((field, index) => (
            <EditFaqRow
              key={field.id}
              index={index}
              control={control}
              onRemove={handleRemoveFaq}
              showRemoveButton={fields.length > 1}
            />
          ))}
          <div className={classes.addButtonRow}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddFaq}
            >
              Add New Question
            </Button>
          </div>
        </>
      )}
    </InfoBox>
  );
};
